import React, { useState } from "react";
import { Alert, Button, Form, Input } from "antd";

import { ancanaApi } from "../../../api/ancanaApi";
import { INewLevel } from "../../../types/models/presentations";

interface Props {
  /** The slide's level, 1-based, as the office shows it. */
  number: number;
  newLevel: INewLevel;
  /** Called with the new level's id once it exists. */
  onAdded: (levelId: number) => void;
}

/**
 * Adds the storey a floor-plan slide asks for, from the slide itself, so the
 * Prepare screen never sends anybody to the design's drawer and back.
 *
 * A level belongs to the design, not the listing: every home presenting the
 * design gains the slide, and each needs the level's plan before it can
 * generate. That is said before anybody adds one. The name prints on the slide,
 * so it is asked for here.
 */
const AddLevelForm = ({ number, newLevel, onAdded }: Props) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const add = async (values: { name__es: string; name__en?: string }) => {
    setSaving(true);
    setError(undefined);
    try {
      const { data } = await ancanaApi.post("design-levels/", {
        design: newLevel.design,
        index: newLevel.index,
        layout_override: "",
        name__es: values.name__es.trim(),
        name__en: (values.name__en || "").trim(),
      });
      onAdded(data.id);
    } catch (exc: any) {
      const detail = exc?.response?.data;
      setError(typeof detail === "string" ? detail
        : detail?.detail || (detail && Object.values(detail).flat().join(" "))
        || "The level could not be added.");
    } finally {
      setSaving(false);
    }
  };

  const design = newLevel.design_name ? `the design ${newLevel.design_name}` : "this home's design";
  const others = newLevel.homes - 1;

  return (
    <div className="add-level">
      <p>
        If the home has a level {number}, add it here. It is added to {design}
        {others > 0
          ? `, so the ${others} other home${others === 1 ? "" : "s"} of this design get this slide too. `
            + "Until its floor plan is published, none of them can generate; publishing it on the design covers them all."
          : ". Until its floor plan is published, this brochure cannot generate."}
      </p>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Form layout="inline" onFinish={add} requiredMark={false}>
        <Form.Item name="name__es" label="Name (Spanish)"
                   rules={[{ required: true, whitespace: true, message: "It prints on the slide." }]}>
          <Input maxLength={128} placeholder="Planta alta" />
        </Form.Item>
        <Form.Item name="name__en" label="Name (English)">
          <Input maxLength={128} placeholder="Upper floor" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            Add level {number} and make its plan
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default AddLevelForm;
